import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

interface AdEntityRow {
    entityId: string;
    name: string;
    spend: number;
    impressions: number;
    clicks: number;
    ctr: number;
    cpc: number;
    roas: number;
}

export const CreativePerformanceTableChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, entities, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const tableData = useMemo((): AdEntityRow[] => {
        if (!finance) return [];

        const adItems = (finance.detailedBreakdown || finance.breakdown || []).filter(
            (item: any) => ['advertising', 'ad_spend', 'marketing'].includes(item.category)
        );

        const entityMap: Record<string, {
            name: string;
            spend: number;
            revenue: number;
            impressions: number;
            clicks: number;
        }> = {};

        for (const item of adItems) {
            const entityId = item.entityId as string;
            if (!entityMap[entityId]) {
                entityMap[entityId] = {
                    name: (item.entityName as string) || entityId,
                    spend: 0,
                    revenue: 0,
                    impressions: 0,
                    clicks: 0,
                };
            }

            const value = Math.abs(Number(item.value) || 0);
            if (item.type === 'EXPENSE') {
                entityMap[entityId].spend += value;
            } else if (item.type === 'REVENUE') {
                entityMap[entityId].revenue += value;
            }
        }

        if (finance.dailyBreakdown) {
            for (const dateKey of Object.keys(finance.dailyBreakdown)) {
                const day = finance.dailyBreakdown[dateKey];
                if (day.breakdown) {
                    for (const item of day.breakdown) {
                        if (!['advertising', 'ad_spend', 'marketing'].includes(item.category as string)) continue;
                        const entityId = item.entityId as string;
                        if (!entityMap[entityId]) continue;

                        entityMap[entityId].impressions += Number(item.impressions) || 0;
                        entityMap[entityId].clicks += Number(item.clicks) || 0;
                    }
                }
            }
        }

        if (entities) {
            for (const entity of entities) {
                const eId = entity.id as string;
                if (entityMap[eId]) {
                    entityMap[eId].name = (entity.name as string) || entityMap[eId].name;
                }
            }
        }

        return Object.entries(entityMap)
            .map(([entityId, data]) => {
                const ctr = data.impressions > 0
                    ? Math.round((data.clicks / data.impressions) * 10000) / 100
                    : 0;
                const cpc = data.clicks > 0
                    ? Math.round((data.spend / data.clicks) * 100) / 100
                    : 0;
                const roas = data.spend > 0
                    ? Math.round((data.revenue / data.spend) * 100) / 100
                    : 0;

                return {
                    entityId,
                    name: data.name,
                    spend: data.spend,
                    impressions: data.impressions,
                    clicks: data.clicks,
                    ctr,
                    cpc,
                    roas,
                };
            })
            .sort((a, b) => b.spend - a.spend);
    }, [finance?.breakdown, finance?.dailyBreakdown, entities]);

    const isEmpty = useMemo(() => tableData.length === 0, [tableData]);

    useSetChartMetadata({ isEmpty });

    if (isEmpty) return null;

    return (

        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2 overflow-auto">
                <table className="w-full text-left text-[10px]">
                    <thead>
                        <tr className="border-b border-[var(--border-subtle)]">
                            <th className="py-2 px-2 font-medium text-[var(--text-primary)] text-[10px]">
                                {t('charts.name', 'Name')}
                            </th>
                            <th className="py-2 px-2 font-medium text-[var(--text-primary)] text-[10px] text-right">
                                {t('charts.spend', 'Spend')}
                            </th>
                            <th className="py-2 px-2 font-medium text-[var(--text-primary)] text-[10px] text-right">
                                {t('charts.impressions', 'Impr.')}
                            </th>
                            <th className="py-2 px-2 font-medium text-[var(--text-primary)] text-[10px] text-right">
                                {t('charts.clicks', 'Clicks')}
                            </th>
                            <th className="py-2 px-2 font-medium text-[var(--text-primary)] text-[10px] text-right">
                                {t('charts.ctr', 'CTR')}
                            </th>
                            <th className="py-2 px-2 font-medium text-[var(--text-primary)] text-[10px] text-right">
                                {t('charts.cpc', 'CPC')}
                            </th>
                            <th className="py-2 px-2 font-medium text-[var(--text-primary)] text-[10px] text-right">
                                {t('charts.roas', 'ROAS')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map((row) => (
                            <tr
                                key={row.entityId}
                                className="border-b border-[var(--border-subtle)] last:border-b-0"
                            >
                                <td className="py-2 px-2 text-[10px] text-[var(--text-primary)] truncate max-w-[120px]">
                                    {row.name}
                                </td>
                                <td className="py-2 px-2 text-[10px] text-right text-[var(--text-primary)]">
                                    {formatCompactNumber(row.spend, currencySymbol)}
                                </td>
                                <td className="py-2 px-2 text-[10px] text-right text-[var(--text-primary)]">
                                    {row.impressions.toLocaleString()}
                                </td>
                                <td className="py-2 px-2 text-[10px] text-right text-[var(--text-primary)]">
                                    {row.clicks.toLocaleString()}
                                </td>
                                <td className="py-2 px-2 text-[10px] text-right text-[var(--text-primary)]">
                                    {row.ctr}%
                                </td>
                                <td className="py-2 px-2 text-[10px] text-right text-[var(--text-primary)]">
                                    {formatCurrency(row.cpc, currencySymbol)}
                                </td>
                                <td className="py-2 px-2 text-[10px] text-right font-medium"
                                    style={{
                                        color: row.roas >= 1 ? CHART_COLORS.revenue : CHART_COLORS.expense,
                                    }}
                                >
                                    {row.roas}x
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
